import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuerySample } from '@store/helpers';
import { DEFAULT_PAGE_SIZE } from '@src/utils/constants';
import { updateXHeaderId } from '@utils/storage';

export type SegmentEquipmentSelectItem = {
  id?: number;
  value?: string;
  label?: string;
  code?: string;
};

export type SegmentEquipmentResponse = {
  diagnoses?: SegmentEquipmentSelectItem[];
  equipments?: SegmentEquipmentSelectItem[];
};

export type SaveSegmentEquipmentRequest = {
  diagnosisCodes: string[];
  equipmentItems: string[];
};

export type AcquiredMode = 'ASSIGNMENT_LEVEL' | 'RIDE_LEVEL' | 'STANDING_LEVEL';

export type LockBody = {
  segmentIds: number[];
};

export type GlobalSocket = {
  type: string;
  segmentId?: number;
  rideId?: number;
  dateOfService: string;
  userName: string;
  confirmationCode: string;
  contractorId?: number;
  lockedByUserId?: number;
  currentUser?: boolean;
  memberName: string;
};

export type LockAcquiredBody = LockBody & { source: AcquiredMode };

export type SegmentLockOwner = {
  userId: number;
  userName: string;
  role: string;
};

export type SegmentLockResult = {
  segmentId: number;
  outcome?: 'LOCKED' | 'ALREADY_OWNED' | 'UNLOCKED';
  success?: boolean;
  locked?: boolean;
  owner: SegmentLockOwner | null;
};

export type SegmentLockResponse = {
  lockingActive: boolean;
  results: SegmentLockResult[];
};

type ContractorPortalTableRequest = {
  body?: Record<string, unknown>;
  params?: Record<string, string | number>;
};

type ContractorPortalTableResponse = {
  content: Record<string, unknown>[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
  filter?: Record<string, unknown>;
  sortDir?: string;
  sortBy?: string;
  table?: string;
};

export const ridesApi = createApi({
  reducerPath: 'Rides',
  tagTypes: ['Rides', 'SegmentEquipment'],
  baseQuery: baseQuerySample,
  endpoints: (builder) => ({
    getRideById: builder.query({
      query: ({ id, isPortalVersion = false }) => ({
        url: isPortalVersion
          ? `/v2/rides/assignment/contractor-portal/ride/${id}`
          : `/v1/rides/ride/${id}`,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if ((data as any)?.member?.id) {
            updateXHeaderId('member', (data as any).member.id);
          }
        } catch {
          // Ignore failed ride loads; the request error is handled by callers.
        }
      },
    }),
    getStandingOrderById: builder.query({
      query: ({ id }) => ({
        url: `/v1/rides/standing-order/${id}`,
      }),
    }),
    getStandingOrderRideCode: builder.query({
      query: ({ id }) => ({
        url: `/v1/rides/standing-order/get-ride-code/${id}`,
        responseHandler: 'text',
      }),
      transformResponse: (response: string) => response?.trim?.() || '',
    }),
    getRides: builder.query({
      query: () => ({
        url: `/v1/rides/ride`,
      }),
    }),

    getRideSegmentsIds: builder.query({
      query: ({ rideId }) => ({
        url: `/v1/search/ride/${rideId}/segment-ids`,
      }),
    }),
    createRides: builder.mutation({
      query: ({ body = {} }) => ({
        url: `/v1/rides/ride`,
        method: 'POST',
        body,
      }),
    }),
    createOrUpdateRide: builder.mutation({
      query: ({ id, body = {} }) => ({
        url: `/v1/rides/ride${id ? `/update/${id}` : ''}`,
        method: id ? 'PUT' : 'POST',
        body,
      }),
    }),
    searchRides: builder.mutation({
      query: ({ argument: { body = {} }, params = {} }) => ({
        url: `/v1/search/ride/search-rides`,
        method: 'POST',
        body,
        params: {
          sortBy: 'serviceDate',
          sortDir: 'desc',
          page: 0,
          size: DEFAULT_PAGE_SIZE,
          ...params,
        },
      }),
    }),
    searchStandingOrder: builder.mutation({
      query: ({ argument: { body = {} }, params = {} }) => ({
        url: `/v1/search/ride/search-standing-order`,
        method: 'POST',
        body,
        params: {
          sortBy: 'startDate',
          sortDir: 'desc',
          page: 0,
          size: DEFAULT_PAGE_SIZE,
          ...params,
        },
      }),
    }),
    createOrUpdateStandingOrder: builder.mutation({
      query: ({ id, body = {} }) => ({
        url: `/v1/rides/standing-order/${id ? `update/${id}` : 'create'}`,
        method: id ? 'PUT' : 'POST',
        body,
      }),
    }),
    getRideSegmentNotes: builder.mutation({
      query: ({
        argument: { path, associatedId, recordId },
        body = {},
        params = {},
      }) => ({
        url: `/v1/notes/${path}/${associatedId}/${recordId}`,
        method: 'POST',
        body,
        params: {
          sortBy: 'actionTime',
          sortDir: 'desc',
          page: 0,
          size: DEFAULT_PAGE_SIZE,
          ...params,
        },
      }),
    }),
    getRidesByModulePost: builder.mutation({
      query: ({ argument: { pathName }, body = {}, params = {} }) => ({
        url: `/v1/rides/${pathName}/rides`,
        method: 'POST',
        body,
        params: {
          sortBy: 'rideRequestDate',
          sortDir: 'desc',
          page: 0,
          size: DEFAULT_PAGE_SIZE,
          ...params,
        },
      }),
    }),
    getStandingOrdersByModulePost: builder.mutation({
      query: ({ argument: { pathName }, body = {}, params = {} }) => ({
        url: `/v1/rides/${pathName}/standing-order`,
        method: 'POST',
        body,
        params: {
          sortBy: 'startDate',
          sortDir: 'desc',
          page: 0,
          size: DEFAULT_PAGE_SIZE,
          ...params,
        },
      }),
    }),
    getContractorPortalRides: builder.mutation<
      ContractorPortalTableResponse,
      ContractorPortalTableRequest
    >({
      query: ({ params = {} }) => ({
        url: `/v1/rides/contractor/portal/rides`,
        method: 'POST',
        params: {
          sortBy: 'id',
          sortDir: 'desc',
          page: 0,
          size: DEFAULT_PAGE_SIZE,
          ...params,
        },
      }),
    }),
    getContractorPortalStandingOrders: builder.mutation<
      ContractorPortalTableResponse,
      ContractorPortalTableRequest
    >({
      query: ({ body = {}, params = {} }) => ({
        url: `/v1/rides/contractor/portal/standing-order`,
        method: 'POST',
        body,
        params: {
          sortBy: 'startDate',
          sortDir: 'desc',
          page: 0,
          size: DEFAULT_PAGE_SIZE,
          ...params,
        },
      }),
    }),
    getGeneratedRide: builder.query({
      query: ({ id }) => ({
        url: `/v1/rides/standing-order/generate/${id}`,
      }),
    }),
    updateRideSegmentStatus: builder.mutation({
      query: ({ body = {} }) => ({
        url: `/v1/rides/ride-segments/segment`,
        method: 'POST',
        body,
      }),
    }),
    checkMemberRides: builder.mutation({
      query: ({ body = {} }) => ({
        url: `/v1/search/ride/check-member-rides`,
        method: 'POST',
        body,
      }),
    }),
    activateWillCall: builder.mutation({
      query: ({ param, body }) => {
        return {
          // url: `/v1/rides/assignment/segment/${param}`,
          url: `/v2/rides/assignment/segment/${param}`,
          method: 'POST',
          body,
        };
      },
    }),
    getRideSegmentTimes: builder.query({
      query: ({ segmentId }) => `/v1/rides/ride-segment-times/${segmentId}`,
    }),
    getSegmentEquipment: builder.query<
      SegmentEquipmentResponse,
      { segmentId: number }
    >({
      query: ({ segmentId }) => ({
        url: `/v2/rides/equipment/segment/${segmentId}`,
      }),
      providesTags: (_result, _error, { segmentId }) => [
        { type: 'SegmentEquipment', id: segmentId },
      ],
    }),
    saveSegmentEquipment: builder.mutation<
      void,
      { segmentId: number; body: SaveSegmentEquipmentRequest }
    >({
      query: ({ segmentId, body }) => ({
        url: `/v2/rides/equipment/segment/${segmentId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { segmentId }) => [
        { type: 'SegmentEquipment', id: segmentId },
      ],
    }),
    lockSegment: builder.mutation<
      SegmentLockResponse,
      { body: LockAcquiredBody }
    >({
      query: ({ body }: { body: LockAcquiredBody }) => {
        return {
          url: `/v1/rides/segment-locks/acquire`,
          method: 'POST',
          body,
        };
      },
    }),
    unLockSegment: builder.mutation<unknown, { body: LockBody }>({
      query: ({ body }: { body: LockBody }) => {
        return {
          url: `/v1/rides/segment-locks/release`,
          method: 'POST',
          body,
        };
      },
    }),
    checkLockSegmentStatus: builder.mutation<
      SegmentLockResponse,
      { body: LockBody }
    >({
      query: ({ body }: { body: LockBody }) => {
        return {
          url: `/v1/rides/segment-locks/status`,
          method: 'POST',
          body,
        };
      },
    }),
    requestPermission: builder.mutation<
      SegmentLockResponse,
      { body: GlobalSocket }
    >({
      query: ({ body }: { body: GlobalSocket }) => {
        return {
          url: `/v1/rides/segment-locks/notify-unlock-ride`,
          // TODO open this after updates
          // url: `/websocket/notify-unlock-ride`,
          method: 'POST',
          body,
        };
      },
    }),
    unlockAllUserLocks: builder.mutation<void, { userId: number }>({
      query: ({ userId }: { userId: number }) => {
        return {
          url: `/v1/rides/segment-locks/release-by-user/${userId}`,
          method: 'DELETE',
        };
      },
    }),
  }),
});

export const {
  useSearchStandingOrderMutation,
  useSearchRidesMutation,
  useGetRideSegmentsIdsQuery,
  useLazyGetRideSegmentsIdsQuery,
  useLazyGetRidesQuery,
  useCreateRidesMutation,
  useLazyGetRideByIdQuery,
  useLazyGetStandingOrderRideCodeQuery,
  useCreateOrUpdateRideMutation,
  useLazyGetStandingOrderByIdQuery,
  useCreateOrUpdateStandingOrderMutation,
  useGetRideSegmentNotesMutation,
  useGetRidesByModulePostMutation,
  useGetStandingOrdersByModulePostMutation,
  useGetContractorPortalRidesMutation,
  useGetContractorPortalStandingOrdersMutation,
  useLazyGetGeneratedRideQuery,
  useUpdateRideSegmentStatusMutation,
  useCheckMemberRidesMutation,
  useActivateWillCallMutation,
  useLazyGetRideSegmentTimesQuery,
  useGetSegmentEquipmentQuery,
  useSaveSegmentEquipmentMutation,
  useLockSegmentMutation,
  useUnLockSegmentMutation,
  useCheckLockSegmentStatusMutation,
  useRequestPermissionMutation,
  useUnlockAllUserLocksMutation,
} = ridesApi;
